import React, { useState, useMemo, useRef, useEffect } from 'react';
import { 
  FileText, 
  Plus, 
  Edit3, 
  Trash2, 
  Eye, 
  EyeOff, 
  Search, 
  CheckCircle2, 
  AlertCircle, 
  Sparkles, 
  Image as ImageIcon, 
  Link as LinkIcon, 
  Heading1, 
  Heading2, 
  Heading3, 
  Bold, 
  Italic, 
  Underline, 
  List, 
  ListOrdered, 
  Quote, 
  Tag, 
  Globe, 
  Calendar, 
  Clock, 
  User, 
  ShoppingBag, 
  X, 
  Save, 
  ChevronRight,
  ExternalLink,
  Layers,
  Code2,
  Eraser,
  HelpCircle,
  ArrowRight,
  Upload
} from 'lucide-react';
import { useStore } from '../../context/StoreContext';
import { useLanguage } from '../../context/LanguageContext';
import { BLOG_CATEGORIES } from '../../data/blogsData';
import RichWysiwygEditor from './RichWysiwygEditor';

const DEFAULT_ARTICLE_STATE = {
  title: '',
  titleUrdu: '',
  titleEn: '',
  category: 'herbs',
  categoryUrdu: 'جڑی بوٹیاں و نباتات',
  coverImage: 'https://images.unsplash.com/photo-1620916566398-39f1143ab7be?w=1200&auto=format&fit=crop&q=80',
  featuredImage: 'https://images.unsplash.com/photo-1620916566398-39f1143ab7be?w=1200&auto=format&fit=crop&q=80',
  author: 'حکیم مولانا سید عبدالوہاب شاہ شیرازی',
  authorTitle: 'فاضل طب والجراحت (FTJ) - ماہر قانون مفرد اعضاء',
  publishedDate: new Date().toISOString().slice(0, 10),
  readTime: '5 منٹ',
  readTimeMinutes: 5,
  status: 'published',
  linkedProductId: '',
  excerpt: '',
  excerptUrdu: '',
  excerptEn: '',
  tags: [],
  tagsInput: '',
  seoTitle: '',
  seoDescription: '',
  seoKeywords: '',
  seo: {
    metaTitle: '',
    metaDescription: '',
    focusKeywords: ''
  },
  slug: '',
  content: '',
  contentHtml: ''
};

const getCategoryName = (catId) => {
  const cat = BLOG_CATEGORIES.find(c => c.id === catId);
  return cat ? cat.nameUrdu : 'جڑی بوٹیاں و نباتات';
};

const BlogManager = () => {
  const { lang, t } = useLanguage();
  const { blogs = [], addBlog, updateBlog, deleteBlog, products = [] } = useStore();

  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');

  const [isEditorOpen, setIsEditorOpen] = useState(false);
  const [editingBlogId, setEditingBlogId] = useState(null);
  const [formData, setFormData] = useState(DEFAULT_ARTICLE_STATE);
  const [editorTab, setEditorTab] = useState('write'); // 'write', 'seo', 'preview'
  const [isCodeMode, setIsCodeMode] = useState(false); // false = Visual WYSIWYG, true = Raw HTML Code
  const [deleteConfirmId, setDeleteConfirmId] = useState(null);
  const [saveSuccessMsg, setSaveSuccessMsg] = useState(false);
  const visualEditorRef = useRef(null);
  const textareaRef = useRef(null);
  const coverImageFileInputRef = useRef(null);
  const inArticleImageFileInputRef = useRef(null);
  const [isUploadingCover, setIsUploadingCover] = useState(false);
  const [isUploadingArticleImg, setIsUploadingArticleImg] = useState(false);
  const [coverImageMode, setCoverImageMode] = useState('upload'); // 'upload' or 'url'

  // Synchronize visual editor contents when opening editor or switching modes
  useEffect(() => {
    if (isEditorOpen && editorTab === 'write' && !isCodeMode) {
      // Small timeout to allow DOM node to mount
      const timer = setTimeout(() => {
        if (visualEditorRef.current) {
          const contentToLoad = formData.contentHtml || formData.content || '';
          if (visualEditorRef.current.innerHTML !== contentToLoad) {
            visualEditorRef.current.innerHTML = contentToLoad;
          }
        }
      }, 50);
      return () => clearTimeout(timer);
    }
  }, [isEditorOpen, editorTab, isCodeMode, editingBlogId]);

  // Filtered Blogs list
  const filteredBlogs = useMemo(() => {
    return blogs.filter(blog => {
      const q = searchQuery.toLowerCase().trim();
      const titleMatch = (blog.titleUrdu || blog.title || '').toLowerCase().includes(q);
      const titleEnMatch = (blog.titleEn || '').toLowerCase().includes(q);
      const authorMatch = (blog.author || '').toLowerCase().includes(q);
      const tagsMatch = blog.tags?.some(tag => tag.toLowerCase().includes(q));

      const matchSearch = !q || titleMatch || titleEnMatch || authorMatch || tagsMatch;
      const matchCat = selectedCategory === 'all' || blog.category === selectedCategory;
      const matchStatus = statusFilter === 'all' || blog.status === statusFilter;

      return matchSearch && matchCat && matchStatus;
    });
  }, [blogs, searchQuery, selectedCategory, statusFilter]);

  // Open editor for new article
  const handleNewArticle = () => {
    setEditingBlogId(null);
    setIsCodeMode(false);
    setFormData({
      ...DEFAULT_ARTICLE_STATE,
      publishedDate: new Date().toISOString().slice(0, 10)
    });
    setEditorTab('write');
    setIsEditorOpen(true);
  };

  // Open editor to edit existing article
  const handleEditArticle = (blog) => {
    setEditingBlogId(blog.id);
    setIsCodeMode(false);
    const loadedTitle = blog.titleUrdu || blog.title || '';
    const loadedExcerpt = blog.excerptUrdu || blog.excerpt || '';
    const loadedImage = blog.coverImage || blog.featuredImage || '/images/products/kalonji.jpg';
    const loadedContent = blog.contentHtml || blog.content || '';

    setFormData({
      ...DEFAULT_ARTICLE_STATE,
      ...blog,
      title: loadedTitle,
      titleUrdu: loadedTitle,
      excerpt: loadedExcerpt,
      excerptUrdu: loadedExcerpt,
      coverImage: loadedImage,
      featuredImage: loadedImage,
      content: loadedContent,
      contentHtml: loadedContent,
      tagsInput: Array.isArray(blog.tags) ? blog.tags.join('، ') : (blog.tags || ''),
      seo: blog.seo || {
        metaTitle: blog.seoTitle || loadedTitle,
        metaDescription: blog.seoDescription || loadedExcerpt,
        focusKeywords: blog.seoKeywords || ''
      }
    });
    setEditorTab('write');
    setIsEditorOpen(true);
  };

  // Visual Editor Command Executer
  const execVisualCommand = (cmd, val = null) => {
    if (visualEditorRef.current) {
      visualEditorRef.current.focus();
      document.execCommand(cmd, false, val);
      setFormData(prev => ({ 
        ...prev, 
        contentHtml: visualEditorRef.current.innerHTML,
        content: visualEditorRef.current.innerHTML
      }));
    }
  };

  // Visual Headings and Blocks
  const formatVisualBlock = (tag) => {
    if (visualEditorRef.current) {
      visualEditorRef.current.focus();
      document.execCommand('formatBlock', false, tag);
      setFormData(prev => ({ 
        ...prev, 
        contentHtml: visualEditorRef.current.innerHTML,
        content: visualEditorRef.current.innerHTML 
      }));
    }
  };

  // Insert Link in Visual Mode
  const insertVisualLink = () => {
    const url = prompt('ویب سائٹ کا لنک درج کریں (URL):', 'https://');
    if (url && url !== 'https://') {
      execVisualCommand('createLink', url);
    }
  };

  // Insert Rich Tibbi Callouts into Visual Editor
  const insertVisualCallout = (type) => {
    let calloutHtml = '';
    if (type === 'tibbi') {
      calloutHtml = `
        <div class="my-4 p-4 rounded-2xl bg-emerald-50 border-r-4 border-emerald-600 text-emerald-950 font-urdu">
          <h4 class="font-bold text-emerald-900 mb-1 text-base">🌿 طبی نکتہ:</h4>
          <p class="text-sm leading-relaxed text-emerald-900">یہاں اپنا مستند طبی نکتہ اور اصولی رہنمائی درج کریں...</p>
        </div><p><br></p>
      `;
    } else if (type === 'nuskha') {
      calloutHtml = `
        <div class="my-4 p-4 rounded-2xl bg-amber-50 border-r-4 border-amber-600 text-amber-950 font-urdu">
          <h4 class="font-bold text-amber-900 mb-1 text-base">🍯 مجرب نسخہ و طریقہ استعمال:</h4>
          <p class="text-sm leading-relaxed text-amber-900">نسخہ کے اجزاء اور استعمال کی درست ترکیب یہاں لکھیں...</p>
        </div><p><br></p>
      `;
    } else if (type === 'precaution') {
      calloutHtml = `
        <div class="my-4 p-4 rounded-2xl bg-rose-50 border-r-4 border-rose-600 text-rose-950 font-urdu">
          <h4 class="font-bold text-rose-900 mb-1 text-base">⚠️ احتیاطی تدابیر و پرہیز:</h4>
          <p class="text-sm leading-relaxed text-rose-900">مریض کے لیے پرہیز اور ضروری انتباہ یہاں درج کریں...</p>
        </div><p><br></p>
      `;
    } else if (type === 'note') {
      calloutHtml = `
        <div class="my-4 p-4 rounded-2xl bg-blue-50 border-r-4 border-blue-600 text-blue-950 font-urdu">
          <h4 class="font-bold text-blue-900 mb-1 text-base">💡 اہم یاد دہانی:</h4>
          <p class="text-sm leading-relaxed text-blue-900">ضروری ہدایات اور رہنمائی یہاں لکھیں...</p>
        </div><p><br></p>
      `;
    }

    if (isCodeMode) {
      setFormData(prev => ({
        ...prev,
        contentHtml: (prev.contentHtml || '') + '\n' + calloutHtml + '\n',
        content: (prev.contentHtml || '') + '\n' + calloutHtml + '\n'
      }));
    } else if (visualEditorRef.current) {
      visualEditorRef.current.focus();
      document.execCommand('insertHTML', false, calloutHtml);
      setFormData(prev => ({ 
        ...prev, 
        contentHtml: visualEditorRef.current.innerHTML,
        content: visualEditorRef.current.innerHTML 
      }));
    }
  };

  // Image compression helper for fast performance and offline storage
  const compressImageFile = (file, maxWidth = 1200) => {
    return new Promise((resolve, reject) => {
      if (!file.type.startsWith('image/')) {
        return reject(new Error('صرف تصویر فائل سپورٹڈ ہے'));
      }
      const reader = new FileReader();
      reader.onload = (event) => {
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement('canvas');
          let w = img.width;
          let h = img.height;
          if (w > maxWidth) {
            h = Math.round((h * maxWidth) / w);
            w = maxWidth;
          }
          canvas.width = w;
          canvas.height = h;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, w, h);
          const compressed = canvas.toDataURL('image/webp', 0.85);
          resolve(compressed);
        };
        img.onerror = () => reject(new Error('تصویر لوڈ کرنے میں مسئلہ'));
        img.src = event.target.result;
      };
      reader.onerror = () => reject(new Error('فائل پڑھنے میں مسئلہ'));
      reader.readAsDataURL(file);
    });
  };

  // Handle Cover / Thumbnail Image Upload from device
  const handleCoverImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setIsUploadingCover(true);
    try {
      const compressed = await compressImageFile(file, 1200);
      setFormData(prev => ({
        ...prev,
        coverImage: compressed,
        featuredImage: compressed
      }));
    } catch (err) {
      alert('تھمبنل تصویر اپلوڈ کرنے میں مسئلہ پیش آیا: ' + err.message);
    } finally {
      setIsUploadingCover(false);
      if (coverImageFileInputRef.current) coverImageFileInputRef.current.value = '';
    }
  };

  // Handle In-Article Image Upload and insert directly into WYSIWYG
  const handleInArticleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setIsUploadingArticleImg(true);
    try {
      const compressed = await compressImageFile(file, 900);
      const imgHtml = `
        <div class="my-6 text-center">
          <img src="${compressed}" alt="طبی معلوماتی تصویر" style="max-height: 480px; margin: 0 auto; border-radius: 1rem; box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1); border: 1px solid #e5e7eb; display: block;" />
          <p class="text-xs text-gray-500 font-urdu mt-2">تصویر کا عنوان / وضاحت</p>
        </div><p><br></p>
      `;

      if (isCodeMode) {
        setFormData(prev => ({
          ...prev,
          contentHtml: (prev.contentHtml || '') + '\n' + imgHtml + '\n',
          content: (prev.contentHtml || '') + '\n' + imgHtml + '\n'
        }));
      } else if (visualEditorRef.current) {
        visualEditorRef.current.focus();
        document.execCommand('insertHTML', false, imgHtml);
        setFormData(prev => ({ 
          ...prev, 
          contentHtml: visualEditorRef.current.innerHTML,
          content: visualEditorRef.current.innerHTML 
        }));
      }
    } catch (err) {
      alert('آرٹیکل میں تصویر شامل کرنے میں مسئلہ پیش آیا: ' + err.message);
    } finally {
      setIsUploadingArticleImg(false);
      if (inArticleImageFileInputRef.current) inArticleImageFileInputRef.current.value = '';
    }
  };

  // Insert Image from URL into WYSIWYG
  const insertImageFromUrl = () => {
    const url = prompt('تصویر کا انٹرنیٹ لنک (URL) درج کریں:', 'https://');
    if (url && url !== 'https://') {
      const imgHtml = `
        <div class="my-6 text-center">
          <img src="${url}" alt="طبی معلوماتی تصویر" style="max-height: 480px; margin: 0 auto; border-radius: 1rem; box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1); border: 1px solid #e5e7eb; display: block;" />
          <p class="text-xs text-gray-500 font-urdu mt-2">تصویر کا عنوان / وضاحت</p>
        </div><p><br></p>
      `;
      if (isCodeMode) {
        setFormData(prev => ({
          ...prev,
          contentHtml: (prev.contentHtml || '') + '\n' + imgHtml + '\n',
          content: (prev.contentHtml || '') + '\n' + imgHtml + '\n'
        }));
      } else if (visualEditorRef.current) {
        visualEditorRef.current.focus();
        document.execCommand('insertHTML', false, imgHtml);
        setFormData(prev => ({ 
          ...prev, 
          contentHtml: visualEditorRef.current.innerHTML,
          content: visualEditorRef.current.innerHTML 
        }));
      }
    }
  };

  // Toggle between Visual and Code mode
  const toggleCodeMode = () => {
    if (!isCodeMode) {
      // Switching from Visual to Code: capture visual editor HTML
      if (visualEditorRef.current) {
        const currentHtml = visualEditorRef.current.innerHTML;
        setFormData(prev => ({ ...prev, contentHtml: currentHtml, content: currentHtml }));
      }
      setIsCodeMode(true);
    } else {
      // Switching from Code to Visual: sync textarea value
      setIsCodeMode(false);
    }
  };

  // Handle Form Submission
  const handleSave = (e) => {
    if (e) e.preventDefault();
    
    // Get latest content from active editor
    let finalContent = formData.contentHtml || formData.content || '';
    if (!isCodeMode && visualEditorRef.current) {
      finalContent = visualEditorRef.current.innerHTML;
    }

    const finalTitle = (formData.titleUrdu || formData.title || '').trim();
    if (!finalTitle) {
      alert('براہ کرم مضمون کا اردو عنوان درج کریں۔');
      return;
    }

    const catObj = BLOG_CATEGORIES.find(c => c.id === formData.category);
    const tagsArray = formData.tagsInput
      ? formData.tagsInput.split(/[،,]/).map(t => t.trim()).filter(Boolean)
      : (formData.tags || []);

    const autoSlug = formData.slug?.trim() || 
      finalTitle.toLowerCase().replace(/[\s\W-]+/g, '-').slice(0, 50);

    const finalExcerpt = (formData.excerptUrdu || formData.excerpt || '').trim();
    const finalImage = (formData.coverImage || formData.featuredImage || '/images/products/kalonji.jpg').trim();

    const articlePayload = {
      ...formData,
      title: finalTitle,
      titleUrdu: finalTitle,
      excerpt: finalExcerpt,
      excerptUrdu: finalExcerpt,
      coverImage: finalImage,
      featuredImage: finalImage,
      content: finalContent,
      contentHtml: finalContent,
      categoryUrdu: catObj?.nameUrdu || 'جڑی بوٹیاں و نباتات',
      tags: tagsArray,
      slug: autoSlug,
      seoTitle: formData.seoTitle || finalTitle,
      seoDescription: formData.seoDescription || finalExcerpt
    };

    if (editingBlogId) {
      updateBlog(editingBlogId, articlePayload);
    } else {
      addBlog(articlePayload);
    }

    setSaveSuccessMsg(true);
    setTimeout(() => {
      setSaveSuccessMsg(false);
      setIsEditorOpen(false);
    }, 1000);
  };

  return (
    <div className="space-y-6">
      
      {/* Top Header Card */}
      <div className="bg-white rounded-3xl p-5 sm:p-6 border border-gray-200/80 shadow-sm flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <span className="p-2 bg-emerald-50 text-emerald-700 rounded-xl">
              <FileText className="w-5 h-5" />
            </span>
            <h2 className="text-xl sm:text-2xl font-black font-urdu text-gray-900">
              بلاگ و ہیلتھ آرٹیکلز منیجمنٹ
            </h2>
          </div>
          <p className="text-xs sm:text-sm text-gray-500 font-urdu">
            گوگل ایس ای او (SEO) اور طبی رہنمائی کے لیے مضامین لکھیں، ایڈٹ کریں اور شائع کریں۔
          </p>
        </div>

        <button
          onClick={handleNewArticle}
          className="flex items-center justify-center gap-2 px-5 py-3 rounded-2xl bg-pansar-700 hover:bg-pansar-800 text-white font-bold text-sm font-urdu shadow-md hover:shadow-lg transition cursor-pointer shrink-0"
        >
          <Plus className="w-4 h-4" />
          <span>نیا مضمون لکھیں</span>
        </button>
      </div>

      {/* Stats Quick Badges */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4">
        <div className="bg-white p-4 rounded-2xl border border-gray-200/70 shadow-2xs">
          <span className="text-xs font-bold text-gray-500 font-urdu block">کل مضامین</span>
          <span className="text-2xl font-black text-gray-900 font-sans">{blogs.length}</span>
        </div>
        <div className="bg-emerald-50/70 p-4 rounded-2xl border border-emerald-100 shadow-2xs">
          <span className="text-xs font-bold text-emerald-800 font-urdu block">شائع شدہ (Live)</span>
          <span className="text-2xl font-black text-emerald-900 font-sans">
            {blogs.filter(b => b.status === 'published').length}
          </span>
        </div>
        <div className="bg-amber-50/70 p-4 rounded-2xl border border-amber-100 shadow-2xs">
          <span className="text-xs font-bold text-amber-800 font-urdu block">مسودات (Drafts)</span>
          <span className="text-2xl font-black text-amber-900 font-sans">
            {blogs.filter(b => b.status === 'draft').length}
          </span>
        </div>
        <div className="bg-blue-50/70 p-4 rounded-2xl border border-blue-100 shadow-2xs">
          <span className="text-xs font-bold text-blue-800 font-urdu block">کل قارئین / ویوز</span>
          <span className="text-2xl font-black text-blue-900 font-sans">
            {blogs.reduce((sum, b) => sum + (b.viewsCount || 0), 0).toLocaleString()}
          </span>
        </div>
      </div>

      {/* Filter and Search Bar */}
      <div className="bg-white p-4 rounded-2xl border border-gray-200/80 shadow-xs flex flex-col sm:flex-row gap-3 items-center justify-between">
        <div className="relative w-full sm:w-80">
          <Search className="w-4 h-4 text-gray-400 absolute right-3 top-3.5" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="عنوان یا ٹیگز تلاش کریں..."
            className="w-full pr-9 pl-3 py-2.5 rounded-xl border border-gray-200 text-sm font-urdu focus:outline-none focus:border-pansar-600 bg-gray-50/50"
          />
        </div>

        <div className="flex items-center gap-2 w-full sm:w-auto overflow-x-auto pb-1 sm:pb-0">
          <select
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
            className="px-3 py-2 rounded-xl border border-gray-200 text-xs font-urdu bg-white text-gray-700 focus:outline-none cursor-pointer"
          >
            {BLOG_CATEGORIES.map(cat => (
              <option key={cat.id} value={cat.id}>{cat.nameUrdu}</option>
            ))}
          </select>

          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-3 py-2 rounded-xl border border-gray-200 text-xs font-urdu bg-white text-gray-700 focus:outline-none cursor-pointer"
          >
            <option value="all">تمام سٹیٹس</option>
            <option value="published">صرف شائع شدہ</option>
            <option value="draft">صرف ڈرافٹ</option>
          </select>
        </div>
      </div>

      {/* Blog Posts Grid / Table */}
      {filteredBlogs.length === 0 ? (
        <div className="bg-white rounded-3xl p-12 text-center border border-gray-200/80">
          <FileText className="w-12 h-12 text-gray-300 mx-auto mb-3" />
          <h3 className="text-base font-bold text-gray-700 font-urdu mb-1">کوئی مضمون نہیں ملا</h3>
          <p className="text-xs text-gray-500 font-urdu mb-4">آپ نیا مضمون لکھ کر اپنی ویب سائٹ پر شائع کر سکتے ہیں۔</p>
          <button
            onClick={handleNewArticle}
            className="px-4 py-2 bg-pansar-700 text-white rounded-xl text-xs font-bold font-urdu cursor-pointer hover:bg-pansar-800 transition"
          >
            نیا مضمون لکھیں
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {filteredBlogs.map(blog => {
            const linkedProd = products.find(p => p.id === blog.linkedProductId);
            const blogTitle = blog.titleUrdu || blog.title || 'بلا عنوان';
            const blogExcerpt = blog.excerptUrdu || blog.excerpt || 'مضمون کا مکمل خلاصہ پڑھنے کے لیے کلک کریں۔';
            const blogImage = blog.coverImage || blog.featuredImage || 'https://images.unsplash.com/photo-1546069901-ba9599a7e63c?w=900&auto=format&fit=crop&q=80';

            return (
              <div 
                key={blog.id}
                className="bg-white rounded-3xl border border-gray-200/80 shadow-sm hover:shadow-md transition-all overflow-hidden flex flex-col group"
              >
                {/* Cover Image Banner */}
                <div className="relative h-44 bg-gray-100 overflow-hidden">
                  <img
                    src={blogImage}
                    alt={blogTitle}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    onError={(e) => { 
                      e.currentTarget.onerror = null; 
                      e.currentTarget.src = 'https://images.unsplash.com/photo-1546069901-ba9599a7e63c?w=900&auto=format&fit=crop&q=80'; 
                    }}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
                  
                  {/* Category & Status Badges */}
                  <div className="absolute top-3 right-3 flex items-center gap-1.5 flex-wrap">
                    <span className="px-2.5 py-1 rounded-full bg-pansar-900/90 text-amber-300 text-[11px] font-bold font-urdu backdrop-blur-xs">
                      {blog.categoryUrdu || 'جڑی بوٹیاں'}
                    </span>
                    {blog.status === 'published' ? (
                      <span className="px-2 py-0.5 rounded-full bg-emerald-500/90 text-white text-[10px] font-bold font-urdu flex items-center gap-1">
                        <CheckCircle2 className="w-2.5 h-2.5" /> شائع شدہ
                      </span>
                    ) : (
                      <span className="px-2 py-0.5 rounded-full bg-amber-500/90 text-white text-[10px] font-bold font-urdu">
                        مسودہ (Draft)
                      </span>
                    )}
                  </div>

                  {/* Read Time & Views */}
                  <div className="absolute bottom-2.5 left-3 right-3 flex items-center justify-between text-white text-[11px] font-urdu">
                    <span className="flex items-center gap-1">
                      <Clock className="w-3 h-3 text-emerald-300" />
                      {blog.readTime || '5 منٹ'}
                    </span>
                    <span className="flex items-center gap-1">
                      <Eye className="w-3 h-3 text-amber-300" />
                      {blog.viewsCount || 0} ویوز
                    </span>
                  </div>
                </div>

                {/* Card Content */}
                <div className="p-4 sm:p-5 flex-1 flex flex-col justify-between">
                  <div>
                    <h3 className="text-base font-bold text-gray-900 font-urdu line-clamp-2 mb-2 group-hover:text-pansar-800 transition">
                      {blogTitle}
                    </h3>
                    <p className="text-xs text-gray-600 font-urdu line-clamp-3 leading-relaxed mb-3">
                      {blogExcerpt}
                    </p>

                    {/* Linked Product Pill */}
                    {linkedProd && (
                      <div className="mb-3 px-2.5 py-1.5 rounded-xl bg-emerald-50 border border-emerald-100 flex items-center gap-2">
                        <ShoppingBag className="w-3.5 h-3.5 text-emerald-700 shrink-0" />
                        <span className="text-[11px] text-emerald-900 font-urdu font-medium truncate">
                          پراڈکٹ لنک: <strong>{linkedProd.nameUrdu}</strong>
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Actions Footer */}
                  <div className="pt-3 border-t border-gray-100 flex items-center justify-between">
                    <span className="text-[11px] text-gray-400 font-sans">
                      {blog.publishedDate}
                    </span>

                    <div className="flex items-center gap-1.5">
                      <button
                        onClick={() => handleEditArticle(blog)}
                        className="p-2 rounded-xl bg-gray-100 hover:bg-pansar-100 text-gray-700 hover:text-pansar-800 transition cursor-pointer flex items-center gap-1 text-xs font-urdu"
                        title="ترمیم کریں"
                      >
                        <Edit3 className="w-3.5 h-3.5 text-pansar-700" />
                        <span>ایڈٹ کریں</span>
                      </button>

                      {deleteConfirmId === blog.id ? (
                        <div className="flex items-center gap-1">
                          <button
                            onClick={() => {
                              deleteBlog(blog.id);
                              setDeleteConfirmId(null);
                            }}
                            className="px-2 py-1 bg-rose-600 text-white rounded-lg text-[10px] font-bold cursor-pointer"
                          >
                            ہاں ڈیلیٹ
                          </button>
                          <button
                            onClick={() => setDeleteConfirmId(null)}
                            className="px-2 py-1 bg-gray-200 text-gray-700 rounded-lg text-[10px] cursor-pointer"
                          >
                            نہیں
                          </button>
                        </div>
                      ) : (
                        <button
                          onClick={() => setDeleteConfirmId(blog.id)}
                          className="p-2 rounded-xl bg-gray-100 hover:bg-rose-100 text-gray-700 hover:text-rose-600 transition cursor-pointer"
                          title="ڈیلیٹ کریں"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* ========================================================================= */}
      {/* ARTICLE WRITER & VISUAL WYSIWYG EDITOR FULL-PAGE VIEW */}
      {/* ========================================================================= */}
      {isEditorOpen && (
        <div className="fixed inset-0 z-50 bg-gray-50 overflow-y-auto flex flex-col animate-fade-in text-right" dir="rtl">
          
          {/* Sticky Top Header Bar */}
          <div className="sticky top-0 z-40 bg-white border-b border-gray-200 px-3 sm:px-8 h-16 shadow-xs flex items-center">
            <div className="max-w-6xl mx-auto w-full flex items-center justify-between gap-3 sm:gap-4">
              
              {/* Back Button & Title */}
              <div className="flex items-center gap-2 sm:gap-3 min-w-0 flex-1">
                <button
                  type="button"
                  onClick={() => setIsEditorOpen(false)}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 sm:px-3.5 sm:py-2 rounded-xl bg-gray-100 hover:bg-gray-200 text-gray-700 hover:text-gray-900 font-urdu font-bold text-xs sm:text-sm transition cursor-pointer shrink-0"
                >
                  <ArrowRight className="w-4 h-4" />
                  <span className="hidden sm:inline">واپس مضامین لسٹ پر جائیں</span>
                  <span className="sm:hidden">واپس</span>
                </button>
                <div className="hidden sm:block h-6 w-px bg-gray-200 shrink-0" />
                <div className="min-w-0 flex-1">
                  <h1 className="font-bold text-xs sm:text-base font-urdu text-pansar-900 flex items-center gap-1.5 truncate">
                    <FileText className="w-4 h-4 text-pansar-700 shrink-0" />
                    <span className="truncate">{editingBlogId ? `مضمون میں ترمیم: ${formData.titleUrdu || 'مضمون'}` : 'نیا طبی و سائنسی مضمون لکھیں'}</span>
                  </h1>
                </div>
              </div>

              {/* Top Action Buttons */}
              <div className="flex items-center gap-2 shrink-0">
                {saveSuccessMsg && (
                  <span className="text-xs text-emerald-700 font-bold bg-emerald-100 px-2.5 py-1 sm:px-3 sm:py-1.5 rounded-xl flex items-center gap-1 animate-pulse font-urdu whitespace-nowrap">
                    <CheckCircle2 className="w-3.5 h-3.5" /> محفوظ ہو گیا!
                  </span>
                )}
                <button
                  type="button"
                  onClick={() => setIsEditorOpen(false)}
                  className="px-3 sm:px-4 py-1.5 sm:py-2 rounded-xl border border-gray-200 text-gray-600 hover:bg-gray-100 text-xs sm:text-sm font-bold font-urdu transition cursor-pointer whitespace-nowrap shrink-0"
                >
                  منسوخ کریں
                </button>
                <button
                  type="button"
                  onClick={handleSave}
                  className="px-3.5 sm:px-5 py-1.5 sm:py-2 bg-pansar-700 hover:bg-pansar-800 text-white rounded-xl text-xs sm:text-sm font-bold font-urdu flex items-center gap-1.5 shadow-sm transition cursor-pointer whitespace-nowrap shrink-0"
                >
                  <Save className="w-4 h-4" />
                  <span>محفوظ کریں (Save)</span>
                </button>
              </div>

            </div>
          </div>

          {/* Mode Tabs Sub-Header */}
          <div className="bg-white border-b border-gray-200 shadow-2xs">
            <div className="max-w-6xl mx-auto px-4 sm:px-8 flex items-center gap-2 sm:gap-4">
              <button
                type="button"
                onClick={() => setEditorTab('write')}
                className={`py-3.5 px-4 text-xs sm:text-sm font-bold font-urdu border-b-2 transition cursor-pointer flex items-center gap-2 ${
                  editorTab === 'write' ? 'border-pansar-700 text-pansar-900' : 'border-transparent text-gray-500 hover:text-gray-800'
                }`}
              >
                <Edit3 className="w-4 h-4" />
                <span>مضمون و بصری ایڈیٹر (Content & Writing)</span>
              </button>

              <button
                type="button"
                onClick={() => {
                  if (!isCodeMode && visualEditorRef.current) {
                    const currentHtml = visualEditorRef.current.innerHTML;
                    setFormData(prev => ({ ...prev, contentHtml: currentHtml, content: currentHtml }));
                  }
                  setEditorTab('seo');
                }}
                className={`py-3.5 px-4 text-xs sm:text-sm font-bold font-urdu border-b-2 transition cursor-pointer flex items-center gap-2 ${
                  editorTab === 'seo' ? 'border-pansar-700 text-pansar-900' : 'border-transparent text-gray-500 hover:text-gray-800'
                }`}
              >
                <Globe className="w-4 h-4" />
                <span>گوگل ایس ای او ترتیبات (SEO Settings)</span>
              </button>

              <button
                type="button"
                onClick={() => {
                  if (!isCodeMode && visualEditorRef.current) {
                    const currentHtml = visualEditorRef.current.innerHTML;
                    setFormData(prev => ({ ...prev, contentHtml: currentHtml, content: currentHtml }));
                  }
                  setEditorTab('preview');
                }}
                className={`py-3.5 px-4 text-xs sm:text-sm font-bold font-urdu border-b-2 transition cursor-pointer flex items-center gap-2 ${
                  editorTab === 'preview' ? 'border-pansar-700 text-pansar-900' : 'border-transparent text-gray-500 hover:text-gray-800'
                }`}
              >
                <Eye className="w-4 h-4" />
                <span>مکمل لائیو پیش نظارہ (Live Preview)</span>
              </button>
            </div>
          </div>

          {/* Full Page Main Body */}
          <div className="flex-1 max-w-6xl mx-auto w-full p-4 sm:p-8 space-y-6 pb-24">
              
              {/* TAB 1: WRITE CONTENT (VISUAL WYSIWYG) */}
              {editorTab === 'write' && (
                <div className="space-y-6">
                  
                  {/* Card 1: Titles, Categories & Cover Image */}
                  <div className="bg-white p-5 sm:p-7 rounded-3xl border border-gray-200/80 shadow-xs space-y-5">
                    <div className="border-b border-gray-100 pb-3">
                      <h2 className="text-base font-bold text-pansar-900 font-urdu flex items-center gap-2">
                        <span className="w-2 h-2 rounded-full bg-pansar-600"></span>
                        مضمون کا عنوان اور بنیادی معلومات (Article Details)
                      </h2>
                    </div>

                    {/* Title & Category */}
                    <div className="grid grid-cols-1 sm:grid-cols-12 gap-4">
                      <div className="sm:col-span-8">
                        <label className="block text-xs font-bold text-gray-700 font-urdu mb-1">
                          مضمون کا اردو عنوان (Urdu Title) *
                        </label>
                        <input
                          type="text"
                          value={formData.titleUrdu}
                          onChange={(e) => setFormData({ ...formData, titleUrdu: e.target.value, title: e.target.value })}
                          placeholder="مثلاً: کلونجی کے طبی فوائد، نبویؐ رہنمائی اور جدید سائنسی تحقیقات"
                          className="w-full px-3.5 py-2.5 rounded-xl border border-gray-300 text-sm font-urdu font-bold focus:outline-none focus:border-pansar-600 focus:ring-1 focus:ring-pansar-600"
                          required
                        />
                      </div>

                      <div className="sm:col-span-4">
                        <label className="block text-xs font-bold text-gray-700 font-urdu mb-1">
                          کیٹیگری (Category)
                        </label>
                        <select
                          value={formData.category}
                          onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                          className="w-full px-3 py-2.5 rounded-xl border border-gray-300 text-xs font-urdu bg-white focus:outline-none focus:border-pansar-600 cursor-pointer"
                        >
                          {BLOG_CATEGORIES.filter(c => c.id !== 'all').map(cat => (
                            <option key={cat.id} value={cat.id}>{cat.nameUrdu}</option>
                          ))}
                        </select>
                      </div>
                    </div>

                    {/* English Title & Cover Image */}
                    <div>
                      <label className="block text-xs font-bold text-gray-700 font-urdu mb-1">
                        انگریزی عنوان (English Title - Optional)
                      </label>
                      <input
                        type="text"
                        value={formData.titleEn}
                        onChange={(e) => setFormData({ ...formData, titleEn: e.target.value })}
                        placeholder="Health Benefits of Pure Kalonji"
                        className="w-full px-3.5 py-2.5 rounded-xl border border-gray-300 text-xs font-sans focus:outline-none focus:border-pansar-600"
                      />
                    </div>

                    {/* Cover / Thumbnail Image Upload & URL Manager */}
                    <div className="bg-gray-50/70 rounded-2xl p-4 border border-gray-200 space-y-3">
                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <div className="flex items-center gap-2">
                          <div className="w-7 h-7 rounded-lg bg-pansar-800 text-white flex items-center justify-center">
                            <ImageIcon className="w-4 h-4" />
                          </div>
                          <div>
                            <span className="text-xs font-bold text-gray-900 font-urdu block">
                              مضمون کی تھمبنل / کور تصویر (Thumbnail / Cover Image) *
                            </span>
                            <span className="text-[10px] text-gray-500 font-urdu">
                              موبائل/کمپیوٹر سے تصویر اپلوڈ کریں یا انٹرنیٹ سے لنک لگائیں
                            </span>
                          </div>
                        </div>

                        {/* Mode Toggle */}
                        <div className="flex bg-white rounded-xl p-0.5 border border-gray-200 text-[11px] font-urdu shadow-2xs">
                          <button
                            type="button"
                            onClick={() => setCoverImageMode('upload')}
                            className={`px-3 py-1 rounded-lg transition font-bold cursor-pointer ${
                              coverImageMode === 'upload' 
                                ? 'bg-pansar-800 text-white shadow-xs' 
                                : 'text-gray-600 hover:text-gray-900'
                            }`}
                          >
                            موبائل / کمپیوٹر فائل
                          </button>
                          <button
                            type="button"
                            onClick={() => setCoverImageMode('url')}
                            className={`px-3 py-1 rounded-lg transition font-bold cursor-pointer ${
                              coverImageMode === 'url' 
                                ? 'bg-pansar-800 text-white shadow-xs' 
                                : 'text-gray-600 hover:text-gray-900'
                            }`}
                          >
                            انٹرنیٹ لنک (URL)
                          </button>
                        </div>
                      </div>

                      {/* Upload Box or URL Input */}
                      {coverImageMode === 'upload' ? (
                        <div className="space-y-1.5">
                          <button
                            type="button"
                            disabled={isUploadingCover}
                            onClick={() => coverImageFileInputRef.current?.click()}
                            className="w-full py-3 px-4 rounded-xl border-2 border-dashed border-emerald-300 hover:border-emerald-600 bg-white hover:bg-emerald-50/50 text-pansar-900 font-bold text-xs font-urdu transition flex items-center justify-center gap-2 cursor-pointer shadow-xs"
                          >
                            <Upload className="w-4 h-4 text-emerald-700" />
                            <span>
                              {isUploadingCover ? 'تھمبنل تصویر ایڈجسٹ ہو رہی ہے...' : '+ اپنے کمپیوٹر یا موبائل سے تھمبنل تصویر منتخب کریں'}
                            </span>
                          </button>
                          <input
                            type="file"
                            ref={coverImageFileInputRef}
                            onChange={handleCoverImageUpload}
                            accept="image/*"
                            className="hidden"
                          />
                        </div>
                      ) : (
                        <div className="flex gap-2">
                          <input
                            type="url"
                            value={formData.coverImage}
                            onChange={(e) => setFormData({ ...formData, coverImage: e.target.value, featuredImage: e.target.value })}
                            placeholder="https://example.com/cover-image.jpg"
                            className="flex-1 p-2.5 rounded-xl border border-gray-200 text-xs font-sans focus:border-pansar-700 focus:outline-none text-left dir-ltr bg-white"
                          />
                        </div>
                      )}

                      {/* Thumbnail Preview Tile if image exists */}
                      {formData.coverImage && (
                        <div className="flex items-center gap-3 p-2.5 bg-white rounded-xl border border-gray-200 shadow-2xs">
                          <div className="w-20 h-14 rounded-lg overflow-hidden bg-gray-100 border border-gray-200 shrink-0 flex items-center justify-center">
                            <img
                              src={formData.coverImage}
                              alt="Thumbnail preview"
                              className="w-full h-full object-cover"
                              onError={(e) => {
                                e.currentTarget.onerror = null;
                                e.currentTarget.src = 'https://images.unsplash.com/photo-1546069901-ba9599a7e63c?w=900&auto=format&fit=crop&q=80';
                              }}
                            />
                          </div>
                          <div className="flex-1 min-w-0">
                            <span className="text-xs font-bold text-gray-800 font-urdu block">
                              منتخب شدہ تھمبنل تصویر
                            </span>
                            <span className="text-[10px] text-gray-400 font-sans truncate block dir-ltr text-left">
                              {typeof formData.coverImage === 'string' && formData.coverImage.startsWith('data:') ? 'ڈیوائس سے اپلوڈ شدہ تصویر' : (formData.coverImage || '')}
                            </span>
                          </div>
                          <button
                            type="button"
                            onClick={() => setFormData({ ...formData, coverImage: '', featuredImage: '' })}
                            className="p-1.5 rounded-lg text-gray-400 hover:text-rose-600 hover:bg-rose-50 transition cursor-pointer"
                            title="تصویر ہٹائیں"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      )}
                    </div>

                    {/* Excerpt / Summary */}
                    <div>
                      <label className="block text-xs font-bold text-gray-700 font-urdu mb-1">
                        مختصر خلاصہ (Article Excerpt - ہوم پیج اور سرچ پر کارڈز میں ظاہر ہوگا)
                      </label>
                      <textarea
                        rows={2}
                        value={formData.excerptUrdu}
                        onChange={(e) => setFormData({ ...formData, excerptUrdu: e.target.value, excerpt: e.target.value })}
                        placeholder="دو تین سطور میں مضمون کا مرکزی پیغام خلاصہ کے طور پر لکھیں جو کارڈز پر نظر آئے گا..."
                        className="w-full px-3.5 py-2.5 rounded-xl border border-gray-300 text-xs font-urdu focus:outline-none focus:border-pansar-600 leading-relaxed"
                      />
                    </div>
                  </div>

                  {/* Card 2: ADVANCED WORDPRESS-STYLE WYSIWYG ARTICLE EDITOR */}
                  <div className="bg-white p-5 sm:p-7 rounded-3xl border border-gray-200/80 shadow-xs space-y-4">
                    <div className="border-b border-gray-100 pb-3 flex flex-wrap items-center justify-between gap-2">
                      <div>
                        <h2 className="text-base font-bold text-pansar-900 font-urdu flex items-center gap-2">
                          <span className="w-2.5 h-2.5 rounded-full bg-emerald-600 animate-pulse"></span>
                          مضمون کا مکمل متن و بصری تحریر (WYSIWYG Article Editor)
                        </h2>
                        <p className="text-xs text-gray-500 font-urdu mt-0.5">
                          ورڈپریس طرز کا مکمل ایڈیٹر: فونٹ سائز، فونٹ سٹائل، دائیں/بائیں سمت، رنگ، جدول (Tables)، ویڈیوز اور طبی باکسز
                        </p>
                      </div>
                      <span className="px-3 py-1 bg-emerald-50 text-emerald-800 rounded-xl text-xs font-bold font-urdu border border-emerald-100">
                        ⚡ ایڈوانسڈ ورڈپریس موڈ
                      </span>
                    </div>

                    <RichWysiwygEditor
                      value={formData.contentHtml || formData.content || ''}
                      onChange={(newHtml) => {
                        setFormData(prev => ({
                          ...prev,
                          contentHtml: newHtml,
                          content: newHtml
                        }));
                      }}
                      isCodeMode={isCodeMode}
                      onToggleCodeMode={toggleCodeMode}
                      placeholder="یہاں مضمون کا تفصیلی متن لکھیں۔ اوپر دیے گئے ٹولز سے سرخیاں، فونٹ سائز، تصاویر، لنکس، جدول اور طبی باکسز وغیرہ شامل کریں..."
                    />
                  </div>

                  {/* Card 3: Metadata Row: Author, Status, Linked Product */}
                  <div className="bg-white p-5 sm:p-7 rounded-3xl border border-gray-200/80 shadow-xs space-y-4">
                    <div className="border-b border-gray-100 pb-3">
                      <h2 className="text-base font-bold text-pansar-900 font-urdu flex items-center gap-2">
                        <span className="w-2 h-2 rounded-full bg-pansar-600"></span>
                        مصنف اور شائع کرنے کا سٹیٹس (Publishing & Author)
                      </h2>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                      <div>
                        <label className="block text-xs font-bold text-gray-700 font-urdu mb-1">
                          مصنف / طبیب کا نام
                        </label>
                        <input
                          type="text"
                          value={formData.author}
                          onChange={(e) => setFormData({ ...formData, author: e.target.value })}
                          className="w-full px-3.5 py-2.5 rounded-xl border border-gray-300 text-xs font-urdu"
                        />
                      </div>

                      <div>
                        <label className="block text-xs font-bold text-gray-700 font-urdu mb-1">
                          مربوط پراڈکٹ (Linked Store Product)
                        </label>
                        <select
                          value={formData.linkedProductId}
                          onChange={(e) => setFormData({ ...formData, linkedProductId: e.target.value })}
                          className="w-full px-3.5 py-2.5 rounded-xl border border-gray-300 text-xs font-urdu bg-white cursor-pointer"
                        >
                          <option value="">-- کوئی پراڈکٹ نہیں --</option>
                          {products.map(p => (
                            <option key={p.id} value={p.id}>{p.nameUrdu}</option>
                          ))}
                        </select>
                      </div>

                      <div>
                        <label className="block text-xs font-bold text-gray-700 font-urdu mb-1">
                          شائع کرنے کا سٹیٹس
                        </label>
                        <select
                          value={formData.status}
                          onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                          className="w-full px-3.5 py-2.5 rounded-xl border border-gray-300 text-xs font-urdu bg-white font-bold cursor-pointer"
                        >
                          <option value="published">شائع شدہ (Live on Website)</option>
                          <option value="draft">ڈرافٹ (Draft - صرف ایڈمن کو نظر آئے)</option>
                        </select>
                      </div>
                    </div>
                  </div>

                </div>
              )}

              {/* TAB 2: SEO SETTINGS */}
              {editorTab === 'seo' && (
                <div className="bg-white p-5 sm:p-8 rounded-3xl border border-gray-200/80 shadow-xs space-y-6">
                  <div className="p-4 bg-emerald-50/70 border border-emerald-200 rounded-2xl text-xs text-emerald-900 font-urdu leading-relaxed">
                    <strong>گوگل ایس ای او گائیڈ:</strong> یہ ترتیبات گوگل سرچ انجن، فیس بک اور واٹس ایپ پر آرٹیکل کے لنک کو پرکشش اور اول نمبر پر رینک کرانے میں مدد دیتی ہیں۔
                  </div>

                  {/* Meta Title */}
                  <div>
                    <label className="block text-xs font-bold text-gray-700 font-urdu mb-1">
                      گوگل سرچ کا عنوان (Meta Title)
                    </label>
                    <input
                      type="text"
                      value={formData.seo?.metaTitle || ''}
                      onChange={(e) => setFormData({
                        ...formData,
                        seo: { ...formData.seo, metaTitle: e.target.value }
                      })}
                      placeholder="کلونجی کے طبی فوائد اور نسخہ جات | شیرازی پنسار سٹور"
                      className="w-full px-3.5 py-2.5 rounded-xl border border-gray-300 text-xs font-urdu"
                    />
                    <span className="text-[10px] text-gray-400 font-urdu mt-1 block">
                      تجویز: 50 تا 60 حروف۔ کلک کرنے والے کے لیے پرکشش ہونا چاہیے۔
                    </span>
                  </div>

                  {/* Meta Description */}
                  <div>
                    <label className="block text-xs font-bold text-gray-700 font-urdu mb-1">
                      گوگل سرچ کی تفصیل (Meta Description)
                    </label>
                    <textarea
                      rows={3}
                      value={formData.seo?.metaDescription || ''}
                      onChange={(e) => setFormData({
                        ...formData,
                        seo: { ...formData.seo, metaDescription: e.target.value }
                      })}
                      placeholder="کلونجی کے حیرت انگیز فوائد، نبویؐ طریقے، کھانے کا صحیح وقت اور شیرازی پنسار کے مجرب نسخہ جات پڑھیں..."
                      className="w-full px-3.5 py-2.5 rounded-xl border border-gray-300 text-xs font-urdu leading-relaxed"
                    />
                    <span className="text-[10px] text-gray-400 font-urdu mt-1 block">
                      تجویز: 140 تا 160 حروف۔ گوگل کے نتائج میں عنوان کے نیچے یہی پیراگراف نظر آتا ہے۔
                    </span>
                  </div>

                  {/* Focus Keywords */}
                  <div>
                    <label className="block text-xs font-bold text-gray-700 font-urdu mb-1">
                      کلیدی الفاظ / کی ورڈز (Focus Keywords - کوما لگا کر لکھیں)
                    </label>
                    <input
                      type="text"
                      value={formData.seo?.focusKeywords || ''}
                      onChange={(e) => setFormData({
                        ...formData,
                        seo: { ...formData.seo, focusKeywords: e.target.value }
                      })}
                      placeholder="کلونجی، کلونجی کے فوائد، طب نبوی، ہربل علاج، شیرازی پنسار"
                      className="w-full px-3.5 py-2.5 rounded-xl border border-gray-300 text-xs font-urdu"
                    />
                  </div>

                  {/* Google Preview Snippet Box */}
                  <div className="p-4 bg-gray-50 border border-gray-200 rounded-2xl space-y-1.5">
                    <span className="text-[11px] font-bold text-gray-600 font-urdu block">
                      🔍 گوگل سرچ پر یہ کیسا نظر آئے گا (Google Search Preview):
                    </span>
                    <div className="bg-white p-4 rounded-xl border border-gray-200 shadow-2xs space-y-1">
                      <div className="text-[11px] text-gray-500 font-sans truncate dir-ltr text-left">
                        https://shirazipansar.pk/blog/{formData.slug || 'article-link'}
                      </div>
                      <div className="text-sm font-bold text-blue-800 font-urdu hover:underline cursor-pointer">
                        {formData.seo?.metaTitle || formData.titleUrdu || 'مضمون کا عنوان'}
                      </div>
                      <div className="text-xs text-gray-600 font-urdu leading-relaxed line-clamp-2">
                        {formData.seo?.metaDescription || formData.excerptUrdu || 'مضمون کا مختصر خلاصہ گوگل سرچ پر دکھائی دے گا۔'}
                      </div>
                    </div>
                  </div>

                </div>
              )}

              {/* TAB 3: LIVE PREVIEW */}
              {editorTab === 'preview' && (
                <div className="bg-white p-6 sm:p-10 rounded-3xl border border-gray-200/80 shadow-xs space-y-6">
                  
                  {/* Category & Read Time */}
                  <div className="flex items-center gap-2">
                    <span className="px-3 py-1 bg-emerald-100 text-emerald-800 rounded-full text-xs font-bold font-urdu">
                      {getCategoryName(formData.category)}
                    </span>
                    <span className="text-xs text-gray-400 font-urdu">
                      • {formData.readTimeMinutes || 4} منٹ مطالعہ
                    </span>
                  </div>

                  {/* Main Title */}
                  <h1 className="text-xl sm:text-3xl font-bold font-urdu text-gray-900 leading-tight">
                    {formData.titleUrdu || 'مضمون کا عنوان'}
                  </h1>

                  {/* Author Banner */}
                  <div className="flex items-center gap-3 p-3.5 rounded-2xl bg-gray-50 border border-gray-100">
                    <div className="w-10 h-10 rounded-full bg-pansar-800 text-amber-300 font-bold flex items-center justify-center font-urdu text-sm">
                      شیرازی
                    </div>
                    <div>
                      <h4 className="text-xs font-bold text-gray-900 font-urdu">{formData.author}</h4>
                      <p className="text-[11px] text-gray-500 font-urdu">{formData.authorTitle}</p>
                    </div>
                  </div>

                  {/* Cover Image */}
                  {formData.coverImage && (
                    <div className="rounded-2xl overflow-hidden max-h-96 bg-gray-100">
                      <img
                        src={formData.coverImage}
                        alt=""
                        className="w-full h-full object-cover"
                        onError={(e) => { 
                          e.currentTarget.onerror = null; 
                          e.currentTarget.src = 'https://images.unsplash.com/photo-1546069901-ba9599a7e63c?w=900&auto=format&fit=crop&q=80'; 
                        }}
                      />
                    </div>
                  )}

                  {/* Lead Excerpt */}
                  {formData.excerptUrdu && (
                    <div className="p-4 rounded-2xl bg-emerald-50/70 border-r-4 border-emerald-700 text-emerald-950 font-urdu text-sm leading-relaxed font-semibold">
                      {formData.excerptUrdu}
                    </div>
                  )}

                  {/* Rendered HTML Content */}
                  <div 
                    className="prose prose-emerald max-w-none text-gray-800 font-urdu leading-relaxed text-sm sm:text-base space-y-4"
                    dangerouslySetInnerHTML={{ __html: formData.contentHtml || '<p class="text-gray-400">مضمون کا متن ابھی خالی ہے۔</p>' }}
                  />
                </div>
              )}

            {/* Bottom Actions Card */}
            <div className="bg-white p-5 sm:p-7 rounded-3xl border border-gray-200/80 shadow-xs flex items-center justify-between gap-3">
              <button
                type="button"
                onClick={() => setIsEditorOpen(false)}
                className="px-5 py-2.5 rounded-xl border border-gray-200 text-gray-600 hover:bg-gray-50 text-xs sm:text-sm font-bold font-urdu cursor-pointer transition"
              >
                منسوخ کریں
              </button>

              <button
                type="button"
                onClick={handleSave}
                className="px-7 py-2.5 bg-pansar-700 hover:bg-pansar-800 text-white rounded-xl text-xs sm:text-sm font-bold font-urdu shadow-sm flex items-center gap-2 cursor-pointer transition"
              >
                <Save className="w-4 h-4" />
                <span>مضمون محفوظ اور شائع کریں (Save & Publish)</span>
              </button>
            </div>

          </div>
        </div>
      )}

    </div>
  );
};

export default BlogManager;
